import { httpsCallable } from "firebase/functions";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
  Timestamp,
} from "firebase/firestore";

import { db, functions } from "../firebase";

const createUsernameChangeSessionFn = httpsCallable(functions, "createUsernameChangeSession");
const applyUsernameChangeFn = httpsCallable(functions, "applyUsernameChange");
const createBillingPortalSessionFn = httpsCallable(functions, "createBillingPortalSession");
const createCreatorStripeDashboardLinkFn = httpsCallable(functions, "createCreatorStripeDashboardLink");
const createCreatorConnectOnboardingLinkFn = httpsCallable(functions, "createCreatorConnectOnboardingLink");
const generateBackupRecoveryCodesFn = httpsCallable(functions, "generateBackupRecoveryCodes");
const deactivateAccountFn = httpsCallable(functions, "deactivateAccount");
const deleteAccountFn = httpsCallable(functions, "deleteAccount");
const requestDataExportFn = httpsCallable(functions, "requestDataExport");
const refreshSettingsBillingFn = httpsCallable(functions, "refreshSettingsBilling");
const reactivateAccountFn = httpsCallable(functions, "reactivateAccount");
const syncReferralBadgeStateFn = httpsCallable(functions, "syncReferralBadgeState");

function callableError(err) {
  return err?.message || err?.details || "Request failed.";
}

export async function createUsernameChangeSession({ successUrl, cancelUrl } = {}) {
  try {
    const { data } = await createUsernameChangeSessionFn({ successUrl, cancelUrl });
    return data;
  } catch (err) {
    throw new Error(callableError(err));
  }
}

export async function applyUsernameChange(newHandle) {
  try {
    const { data } = await applyUsernameChangeFn({ newHandle });
    return data;
  } catch (err) {
    throw new Error(callableError(err));
  }
}

export async function openBillingPortal({ returnUrl } = {}) {
  try {
    const { data } = await createBillingPortalSessionFn({ returnUrl });
    if (data?.url) window.location.href = data.url;
    return data;
  } catch (err) {
    throw new Error(callableError(err));
  }
}

export async function openCreatorStripeDashboard() {
  try {
    // Server dual-path: login link if connectAccountId exists, else Account Link onboarding
    const { data } = await createCreatorStripeDashboardLinkFn({
      returnUrl: `${window.location.origin}/settings?section=payouts&payout=return`,
      refreshUrl: `${window.location.origin}/settings?section=payouts&payout=refresh`,
    });
    if (data?.url) window.location.href = data.url;
    return data;
  } catch (err) {
    throw new Error(callableError(err));
  }
}

/** Start Stripe Connect Express hosted onboarding (accounts.create + accountLinks.create). */
export async function startCreatorConnectOnboarding({ returnUrl, refreshUrl } = {}) {
  try {
    const { data } = await createCreatorConnectOnboardingLinkFn({
      returnUrl:
        returnUrl || `${window.location.origin}/dashboard?payout=return`,
      refreshUrl:
        refreshUrl || `${window.location.origin}/creator-onboarding?payout=refresh`,
    });
    if (data?.url) window.location.href = data.url;
    return data;
  } catch (err) {
    throw new Error(callableError(err));
  }
}

export async function generateBackupRecoveryCodes() {
  try {
    const { data } = await generateBackupRecoveryCodesFn({});
    return data;
  } catch (err) {
    throw new Error(callableError(err));
  }
}

export async function deactivateUserAccount() {
  try {
    const { data } = await deactivateAccountFn({});
    return data;
  } catch (err) {
    throw new Error(callableError(err));
  }
}

export async function deleteUserAccount(confirmation) {
  try {
    const { data } = await deleteAccountFn({ confirmation });
    return data;
  } catch (err) {
    throw new Error(callableError(err));
  }
}

export async function requestUserDataExport() {
  try {
    const { data } = await requestDataExportFn({});
    return data;
  } catch (err) {
    throw new Error(callableError(err));
  }
}

export async function refreshBillingFromStripe() {
  try {
    const { data } = await refreshSettingsBillingFn({});
    return data;
  } catch (err) {
    throw new Error(callableError(err));
  }
}

export async function reactivateUserAccount() {
  try {
    const { data } = await reactivateAccountFn({});
    return data;
  } catch (err) {
    throw new Error(callableError(err));
  }
}

export async function syncReferralBadgeState() {
  try {
    const { data } = await syncReferralBadgeStateFn({});
    return data;
  } catch (err) {
    throw new Error(callableError(err));
  }
}

export async function fetchLoginEvents(userId, max = 30) {
  if (!userId) return [];
  try {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 30);

    const snap = await getDocs(
      query(
        collection(db, `loginEvents/${userId}/events`),
        where("timestamp", ">=", Timestamp.fromDate(cutoff)),
        orderBy("timestamp", "desc"),
        limit(max)
      )
    );

    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch {
    try {
      const snap = await getDocs(
        query(collection(db, `loginEvents/${userId}/events`), limit(max))
      );
      return snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0));
    } catch {
      return [];
    }
  }
}

export function formatConsentDate(ts) {
  if (!ts) return null;
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  return date.toLocaleDateString("en-CA", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function formatLoginWhen(ts) {
  if (!ts) return "—";
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const time = date.toLocaleTimeString("en-CA", { hour: "numeric", minute: "2-digit" });
  if (isToday) return `Today, ${time}`;
  return `${date.toLocaleDateString("en-CA", { month: "short", day: "numeric" })}, ${time}`;
}

export function formatRenewDate(iso) {
  if (!iso) return "—";
  const date = new Date(iso);
  return date.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
}

export function formatBillingDate(iso) {
  if (!iso) return "—";
  const date = new Date(iso);
  return date.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });
}

export function centsToCad(cents) {
  return `$${((Number(cents) || 0) / 100).toFixed(2)}`;
}
